/**
 * How a line item refuses its project, and the cities named on it.
 *
 * ## One guard, three modules, one model
 *
 * Migration 067 moved the project off the document header and onto the LINE,
 * because a project names exactly one product and a document sells many.
 * Migration 068 made a line run in a SET of cities rather than one. Both checks
 * live in a single backend file — `src/modules/projects/project-line.guard.ts` —
 * which every line-item module calls: quote lines, invoice lines and deal
 * lines. One guard on that side means one model on this side. A quote-shaped
 * copy of this file would drift from the invoice-shaped one inside a week.
 *
 * ## The refusals, their paths and their sentences — and why none of that collapses
 *
 * ⚠️ **This heading said "SEVEN refusals, TWO paths" and both numbers are now
 * wrong.** Migration 096 added an eighth on a THIRD path, `inclusions`. The
 * counts are deliberately gone rather than incremented: they have been wrong in
 * this repo's docs, in the backend's docs and in two file headers on the same
 * day, and the rule both repos settled on is **read the codes, not the count**.
 * The table below is written against the `throw`s in the order
 * `assertLineProjectUsable` reaches them, so it cannot drift from a number
 * somebody typed; the eighth is listed after it because it is a different guard.
 *
 * Every refusal is a `422`. They differ on two axes, and both matter:
 *
 * - **`path`** decides which CONTROL a form marks. It is `projectId` when the
 *   project is the thing to change and `cityIds` when the cities are. Marking
 *   the wrong one puts a red outline on an input the person cannot fix.
 * - **the sentence** decides where the person GOES. "No such project" sends
 *   somebody hunting for a record that was never there; "that project is
 *   retired" tells them the record is fine and the *choice* is not. Collapsing
 *   these into one "that is not available" sends everybody to the wrong place —
 *   the same reasoning `XERO_QUOTE_REFUSALS` in `xero.ts` is built on.
 *
 * Numbered in the order `assertLineProjectUsable` reaches them, which is also
 * the order they are readable in the source. Verified by reading that function,
 * not by reading a table:
 *
 * | # | when | `path` | kind | fix is on |
 * | - | ---- | ------ | ---- | --------- |
 * | 1 | the product is sold through projects, so the line needs one | `projectId` | `project-required` | this line |
 * | 2 | cities named on a product that has no projects at all | `cityIds` | `cities-on-projectless-product` | this line |
 * | 3 | the project does not exist | `projectId` | `project-missing` | this line |
 * | 4 | the project is retired | `projectId` | `project-retired` | this line |
 * | 5 | the project is for a different product | `projectId` | `project-wrong-product` | this line |
 * | 6 | the project declares no cities at all | `cityIds` | `project-has-no-cities` | **the project** |
 * | 7 | cities the project does not run in | `cityIds` | `cities-not-on-project` | this line |
 *
 * And the eighth, from a different guard — migration 096, release
 * `20260829123506-c323947`:
 *
 * | # | when | `path` | kind | fix is on |
 * | - | ---- | ------ | ---- | --------- |
 * | 8 | the product does not offer this inclusion in the region the line's project covers | `inclusions` | `inclusion-not-offered-in-region` | **the line, the PRODUCT or the PROJECT** |
 *
 * ⚠️ **Refusal 8 is the only one with more than one destination**, which is why
 * it carries a real `error.code` and why {@link LineRefusalFix} has a member of
 * its own for it. See {@link INCLUSION_NOT_OFFERED_IN_REGION_CODE}, which also
 * records the two costs the owner accepted — the region match is a UNION, and
 * the region is a FILTER that must never be printed on a document.
 *
 * Refusal 2 exists because the obvious sentence would be advice nobody can
 * follow: "choose a project first" is useless when the product has no projects
 * and never will. `Meet Up`, `Profile Evaluations` and `Miscellaneous` are the
 * real cases — they are legitimately projectless, and refusal 1 must never fire
 * on them. Whether a product "is sold through projects" is DERIVED server-side
 * from whether any project names it, not read off a flag somebody maintains, so
 * this app cannot predict either branch and should not try.
 *
 * ## ⚠️ Refusals 6 and 7 share a `path` and mean OPPOSITE things
 *
 * This is the sharpest edge in the file, and `path` cannot see it at all.
 *
 * - **6, `Project N declares no cities`** — the PROJECT is incomplete. Nothing
 *   the person can type into this line fixes it; somebody has to open
 *   `/projects/{id}` and give the project its cities, and that somebody is
 *   frequently not the person staring at the line. The honest sentence is "that
 *   project has no cities yet", not "pick different cities" — there are none to
 *   pick, and the city control is empty for exactly that reason.
 * - **7, `Cities 3, 7 are not among…`** — the LINE is wrong and the fix is right
 *   here: drop those cities, or move the line to a project that runs in them.
 *
 * A caller that treats every `cityIds` refusal the same way sends half of them
 * to a control that cannot help — and 6 is the half where the person then
 * concludes the form is broken, because the picker they were told to use has
 * nothing in it. {@link LineRefusal.fixedOn} exists so that branch is a field
 * read rather than three call sites each re-deriving it from `kind`; see its
 * comment for why `kind` alone, though sufficient in principle, is not what a
 * form should be reaching for.
 *
 * ## ⚠️ The path is the reliable half. The kind is a refinement
 *
 * `path` comes off the wire as a field name and is the contract. The KIND is
 * inferred from the server's prose, which is not — so a failed kind match must
 * never discard the refusal. It degrades to `kind: 'unknown'` carrying the
 * server's own sentence, and the form still marks the right control.
 *
 * **That degradation is the load-bearing behaviour of this file, and it must
 * not be "tidied" into returning `null`.** It has already earned itself once,
 * from the outside: this file was written against a published table of five
 * refusals, then six, and mapped the guard's "that project declares no cities"
 * sentence to `unknown` — which is how the backend came to read their own
 * source and find SEVEN throws, and three of their own documents disagreeing
 * with the code and with each other. A model that had swallowed the sentence it
 * did not recognise would have reported nothing, and the defect would still be
 * live. **An unrecognised sentence means "newer than this map", never "not a
 * refusal".** `xero.ts` learned that lesson first and says so in the same words.
 *
 * ## ⚠️ FIVE of seven share one `code`, so we still match on prose
 *
 * Two of the seven — `PROJECT_HAS_NO_CITIES` and `CITIES_NOT_ON_PROJECT` —
 * carry their own code rather than the shared `UNPROCESSABLE_ENTITY`, on a
 * principle worth keeping:
 *
 * > **A refusal earns a code when it routes somewhere the others do not.**
 *
 * Those two are the pair that needed it. Both arrive on `path: 'cityIds'` and
 * they mean opposite things — *the project has no cities at all*, whose fix is
 * on `/projects/{id}` and often belongs to a different person, versus *these
 * particular cities are not on it*, whose fix is right here. `path` cannot
 * separate them, so prose was the only discriminator and prose is the thing
 * this repo says never to match on.
 *
 * The other five keep the shared code deliberately, not by omission: they are
 * fully served by `path` plus the server's own sentence, and asking for codes
 * that changed nothing we render would have spent a contract change on tidiness
 * — `kind` was consumed at **zero** call sites when that was measured, while
 * `xero.ts`'s `destination` was consumed at five.
 *
 * ⚠️ **So this file still matches prose for five of seven**, and that remains a
 * deliberate exception to the switch-on-`code` rule. It is tolerable only
 * because the server's sentence renders **verbatim**: a rewording degrades to
 * `kind: 'unknown'` with the right control marked, never to a wrong action.
 * Prefer `error.code` for the two that have one.
 *
 * ⚠️ And the risk the backend recorded in their own guard, because it is real:
 * **two named and five anonymous invites the next person to text-match the
 * five.** If an eighth refusal needs routing, ask for a code rather than
 * adding a regex.
 *
 * It is tolerable for one specific reason: **the server's own sentence is
 * rendered verbatim either way**, so the cost of the backend rewording one of
 * these is `kind: 'unknown'` — a refusal that shows the right words on the
 * right control with no supporting copy of ours — and never a wrong action or a
 * blank. The failure mode of text matching here is degradation, not
 * misdirection, which is the only shape in which text matching is acceptable.
 *
 * **Per-refusal codes are an OPEN DECISION with the backend, not a settled
 * no.** If they land, `KIND_PATTERNS` becomes a code map, this section goes,
 * and `xero.ts`'s shape applies unchanged.
 *
 * ## Render the SERVER's sentence. Never a paraphrase of ours
 *
 * Every one of the seven names an id or a product number this app cannot
 * derive — "this project sells product 3, but this line sells product 8" is not
 * reconstructible from anything a form holds. Use {@link LineRefusal.message}
 * as the field-level text. Our own copy, keyed on `kind`, belongs BESIDE it as
 * supporting guidance, never in front of it — and for `kind: 'unknown'` there
 * is deliberately no copy of ours at all, because by definition we do not know
 * what happened and inventing a sentence would be a guess rendered as fact.
 *
 * ## ⚠️ A PATCH is validated on its POST-MERGE state
 *
 * The guard is handed the line as it WILL BE, not the request body. Three
 * consequences, and the second one is the trap:
 *
 * - `cityIds` alone is checked against the project the line **already holds**,
 *   so an ordinary city edit is not refused for omitting a project nobody
 *   changed.
 * - **`projectId` alone re-validates the STORED cities against the new
 *   project.** So refusal 7 can name cities the user never sent and cannot see
 *   on screen — a form that only marks what it submitted marks nothing at all.
 * - Sending neither skips the check entirely, which is why a line whose project
 *   has since dropped a city stays editable.
 *
 * **The rule callers follow: send `{ projectId, cityIds }` TOGETHER when moving
 * a line between projects.** {@link citiesLostByMovingProject} is how a form
 * says "moving this line to that project drops Mumbai and Pune" before it
 * sends, and {@link patchRevalidatesStoredCities} is how it notices it was
 * about to walk into the trap.
 *
 * ⚠️ **Migration 096 put INCLUSIONS on the same trap, and every payload builder
 * in this app was missing it.** A product may offer an inclusion only in certain
 * regions, and a line's region comes from its project — so a `projectId`-only
 * PATCH re-validates the STORED inclusions and can be refused naming a field
 * nobody sent. All four line editors coupled `projectId` to `cityIds` and none
 * of them coupled it to `inclusions`; the rule is now
 * **`{ projectId, cityIds, inclusions }` together**, and
 * {@link patchRevalidatesStoredInclusions} is the test.
 *
 * ⚠️ **One exception, and it is not a bug to be fixed:** where the inclusions
 * editor is not rendered because the product offers none, the key stays OMITTED.
 * `inclusions: []` destroys a line's stored rows with a 200, so the refusal —
 * which cannot be fixed from that line anyway — is the lesser cost.
 */

import type { ApiError } from './api';

/**
 * The server's cap on `cityIds` for one line.
 *
 * A denial-of-service bound, not a business rule — the real ceiling is how many
 * cities the line's project declares, which is always far smaller. Do not
 * render it as "you may add up to 200 cities"; it is here so a form can refuse
 * an absurd paste client-side rather than collect a 422 for it.
 */
export const LINE_CITIES_MAX = 200;

/* -------------------------------------------------------------------------
 * The refusal CODES
 * ---------------------------------------------------------------------- */

/**
 * Refusal 6 — the project declares no cities at all. Fix is on `/projects/{id}`.
 *
 * Added by the backend 2026-08-26, with `CITIES_NOT_ON_PROJECT`, because the two
 * share `path: 'cityIds'` and route to different screens.
 */
export const PROJECT_HAS_NO_CITIES_CODE = 'PROJECT_HAS_NO_CITIES';

/** Refusal 7 — cities the project does not run in. Fix is on this line. */
export const CITIES_NOT_ON_PROJECT_CODE = 'CITIES_NOT_ON_PROJECT';

/**
 * An inclusion this product does not offer **in the region the line's project
 * covers**. Migration 096, release `20260829123506-c323947`.
 *
 * ## Why it earned a code, when five of the others did not
 *
 * The rule in this file is that **a refusal earns a code when it routes
 * somewhere the others do not**. This one routes to THREE screens, and the
 * server cannot say which of them the person wants:
 *
 * - **the LINE** — pick a different inclusion, or move the line to a project in
 *   a region that offers this one;
 * - **the PRODUCT** — widen the offer on `/products` → *Inclusions*. Admin-only,
 *   a different screen, and frequently a different person;
 * - **the PROJECT** — give the project the region, on `/projects/{id}`.
 *
 * No other refusal here has more than one destination, which is exactly why
 * {@link LineRefusalFix} needed a member of its own rather than being answered
 * with `'this-line'`.
 *
 * ## ⚠️ What the region split is, and what it is NOT
 *
 * It is a **FILTER on what a line may choose**, never a LABEL. The chosen
 * inclusion prints on the customer's document exactly as it prints today —
 * `(india only)` is the administrator's note about the offer and **must never
 * reach a composed line description.** Nothing in `line-description.ts` or
 * `inclusions.ts` reads a region, and nothing should start to.
 *
 * ⚠️ **The match is a UNION and the split is a DEFAULT, not a GUARANTEE.** On a
 * project spanning India and Africa an India-only inclusion **is** offered, and
 * nothing downstream asserts the work happens in India. Intersection and
 * outright refusal were both put to the owner and both declined — so **write no
 * copy implying the region is enforced per city or per delivery.**
 *
 * ⚠️ **Nothing is retroactive.** An inclusion already recorded on a document
 * goes on printing what it prints today; this fires on a WRITE only.
 */
export const INCLUSION_NOT_OFFERED_IN_REGION_CODE = 'INCLUSION_NOT_OFFERED_IN_REGION';

/**
 * Which control the refusal belongs to. The contract half — this comes off the
 * wire as `details[].path` and is what decides where the error renders.
 *
 * ⚠️ **`'inclusions'` was added for {@link INCLUSION_NOT_OFFERED_IN_REGION_CODE}
 * and it widened SILENTLY at every consumer.** Nothing in `src/` holds a
 * `Record<LineRefusalPath, …>` or an exhaustive `switch` on this union — every
 * call site tests `refusal.path === 'cityIds'` as a boolean — so adding a member
 * compiles green everywhere and the new refusal reaches no control at all unless
 * somebody writes the branch. That is the "a test that cannot fail" shape in
 * type form: **a widened union with no exhaustive reader announces nothing.**
 * If a fourth path is ever added, grep for `refusal.path` and read every hit.
 */
export type LineRefusalPath = 'projectId' | 'cityIds' | 'inclusions';

/**
 * Which refusal it is, inferred from the server's prose.
 *
 * `unknown` is a first-class member, not a failure: it means the path was one
 * of the two but the sentence is not one this file has been taught. See the
 * header — that is the expected outcome for anything the backend ships after
 * this file was last edited, and the refusal is still fully usable.
 */
export type LineRefusalKind =
  | 'project-required'
  | 'cities-on-projectless-product'
  | 'project-missing'
  | 'project-retired'
  | 'project-wrong-product'
  | 'project-has-no-cities'
  | 'cities-not-on-project'
  | 'inclusion-not-offered-in-region'
  | 'unknown';

/**
 * WHERE the fix is, for a refusal that has been recognised.
 *
 * The second axis, and the one `path` cannot express. `path` says which control
 * to outline; this says whether outlining a control is the right response at
 * all.
 *
 * - `this-line`   — the person can fix it in the line editor they are already
 *                   looking at: choose a project, drop a city, move the line.
 *                   Six of the seven.
 * - `the-project` — the line is fine and the PROJECT is incomplete. The fix is
 *                   on `/projects/{id}`, usually by a different person, and
 *                   nothing typed here changes the answer. Refusal 6 alone.
 * - `unknown`     — the sentence was not recognised, so we do not know. **Never
 *                   collapse this into `this-line`.** Six-of-seven is a tempting
 *                   default and it is exactly the wrong one: it would send
 *                   somebody to edit a line over a problem that is not on it,
 *                   with no way to tell the guidance was guessed. The right
 *                   response to `unknown` is the server's sentence and no
 *                   destination copy at all — the same choice `quoteRefusalOf`
 *                   makes by returning `null` rather than defaulting to the
 *                   quote form.
 * - `line-product-or-project` — {@link INCLUSION_NOT_OFFERED_IN_REGION_CODE}
 *                   alone. **THREE screens, and the server cannot say which**:
 *                   change the line, widen the product's offer (admin-only), or
 *                   give the project the region. It is a member of its own
 *                   rather than `'this-line'` because two of the three fixes are
 *                   NOT on this line, and a reader told to fix the line will
 *                   conclude the app is wrong when the inclusion they want is
 *                   simply not offered where this project runs. **Name all
 *                   three, and let the person choose.**
 */
export type LineRefusalFix =
  | 'this-line'
  | 'the-project'
  | 'line-product-or-project'
  | 'unknown';

export interface LineRefusal {
  /** Which control to mark. The reliable half. */
  path: LineRefusalPath;
  /** Which refusal, for supporting copy only. May be `'unknown'`. */
  kind: LineRefusalKind;
  /**
   * Which SCREEN the fix is on. Derived from {@link kind}, exhaustively.
   *
   * `kind` is strictly sufficient to compute this — a caller could switch on it
   * and get the same answer. It is exposed anyway, for two reasons that are
   * about failure modes rather than convenience:
   *
   * 1. **Three line-item forms consume this file** (deal, quote, invoice). Three
   *    private kind→screen maps agree only until one of them is edited, and the
   *    one that drifts is silently wrong rather than broken — the same argument
   *    that keeps the inclusion wording rule in a single module.
   * 2. **A `switch` on `kind` with a `default` quietly absorbs a new kind into
   *    whichever branch the default names.** The map behind this field is a
   *    `Record<LineRefusalKind, LineRefusalFix>`, so an eighth refusal fails at
   *    `tsc` here, in the one place that has the guard's source next to it,
   *    instead of being answered wrongly at three call sites.
   *
   * Read this to choose the guidance; keep reading {@link path} to choose the
   * control. They are different questions and refusal 6 is where they diverge.
   */
  fixedOn: LineRefusalFix;
  /**
   * The server's own sentence for this field, verbatim. Render THIS, not a
   * copy of ours — several refusals name ids the client cannot derive.
   *
   * This is the `details[]` entry for {@link path} (the actionable one, written
   * to sit next to an input). It falls back to the top-level message when the
   * detail is missing, so it is never empty.
   */
  message: string;
  /**
   * The server's TOP-LEVEL sentence, verbatim — the summary form.
   *
   * Kept beside `message` rather than instead of it because the two say
   * genuinely different things on refusal 7: the detail is
   * `Not declared on this project: 4, 9` while this names the project as well.
   * Use it for a banner; use `message` next to the control.
   */
  summary: string;
  /**
   * Refusal 7 only — every offending id, so a form can mark all the wrong chips
   * in one pass instead of the user fixing one and resubmitting to find the
   * next. `[]` for every other refusal, and `[]` whenever parsing was not
   * certain.
   *
   * ⚠️ **Empty is not "no cities are wrong".** Refusal 6 also names a project id
   * in its sentence and also arrives on `cityIds`, and it yields `[]` — because
   * on 6 there is no offending city, there is a project with no cities at all.
   * Branch on `kind` (or {@link fixedOn}) before reading this; an
   * `offendingCityIds.length === 0` test alone cannot tell "nothing to mark"
   * from "we could not parse the list".
   */
  offendingCityIds: number[];
  /**
   * {@link INCLUSION_NOT_OFFERED_IN_REGION_CODE} only — the POSITIONS in the
   * `inclusions` array the caller SENT that the server named, so a form can mark
   * every offending chip in one pass rather than the person fixing one and
   * resubmitting to find the next.
   *
   * ⚠️ **Positions, not ids, and that is deliberate rather than a shortcoming.**
   * These come from the `details[].path` field — `inclusions.0`, `inclusions.2`
   * — which is **structured contract data**, the same indexed shape
   * `inclusions.N.quantity` already arrives on. The alternative was a regular
   * expression over the server's sentence, and this file already carries the
   * argument against that: a matcher for a sentence the server does not send is
   * the one kind of dead code that never announces itself. An indexed path that
   * never arrives yields `[]`, which costs the highlighting and keeps the
   * refusal — a degradation, not a wrong answer.
   *
   * ⚠️ **Map by POSITION in the array you sent, never by position in the
   * draft**, if the two can differ. Every payload builder in this app emits
   * `inclusions` straight from the draft in order, so today they are the same
   * array; a builder that started filtering entries would silently mark the
   * wrong chips, and nothing would fail.
   *
   * `[]` on every other refusal, and `[]` whenever the paths were not indexed.
   * ⚠️ **Empty is not "nothing is wrong"** — the same warning as
   * {@link offendingCityIds}. Branch on {@link kind} first.
   */
  offendingInclusionIndexes: number[];
}

/* -------------------------------------------------------------------------
 * Reading the error
 * ---------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isRefusalPath(value: unknown): value is LineRefusalPath {
  return value === 'projectId' || value === 'cityIds' || value === 'inclusions';
}

/**
 * `inclusions.2` → `2`, and `null` for anything else.
 *
 * The server folds per-entry refusals onto an INDEXED path, so this is how the
 * offending entries are named. Deliberately anchored: only a bare
 * `inclusions.<digits>` prefix counts, so `inclusions.2.quantity` yields `2`
 * (it is still entry 2 that is wrong) while an unrelated key yields `null`.
 */
function inclusionIndexOf(path: unknown): number | null {
  if (typeof path !== 'string') return null;
  const match = /^inclusions\.(\d+)(?:\.|$)/.exec(path);
  const group = match?.[1];
  if (typeof group !== 'string') return null;
  const index = Number(group);
  return Number.isSafeInteger(index) && index >= 0 ? index : null;
}

/**
 * The first `details[]` entry whose path is one of ours.
 *
 * `ApiError.details` is typed as `{path, message}[]`, but that type is a claim:
 * the constructor narrows the payload with `Array.isArray` and nothing checks
 * the entries. So the entries are re-guarded here rather than trusted — the
 * same reason `detailPayload` is `unknown` and read through a guard instead of
 * a cast.
 *
 * `fieldErrors` would find these too, since all seven carry a real path. It is
 * deliberately not used: it flattens to `{field: message}`, loses the ordering
 * that tells us which detail the server led with, and would silently merge two
 * entries sharing a path.
 */
function firstRefusalDetail(error: ApiError): { path: LineRefusalPath; message: string } | null {
  const details: unknown = error.details;
  if (!Array.isArray(details)) return null;

  for (const entry of details) {
    if (!isRecord(entry)) continue;
    if (!isRefusalPath(entry.path)) continue;
    if (typeof entry.message !== 'string') continue;
    return { path: entry.path, message: entry.message };
  }
  return null;
}

/**
 * EVERY `inclusions` detail — the bare path and every indexed one — as one
 * sentence plus the positions named.
 *
 * ⚠️ **Deliberately not `firstRefusalDetail`.** This refusal names every
 * offender rather than the first, and it names them one `details[]` entry at a
 * time; taking the first would show one offending inclusion, the person would
 * remove it, and the next save would refuse again on the second. Reading them
 * all is the whole reason for a separate collector.
 *
 * Messages are joined in wire order and de-duplicated — a server that repeats
 * one summary across three indexed entries should not print it three times.
 * `null` when there is no `inclusions` detail at all.
 */
function inclusionRefusalDetails(
  error: ApiError,
): { message: string; indexes: number[] } | null {
  const details: unknown = error.details;
  if (!Array.isArray(details)) return null;

  const messages: string[] = [];
  const indexes: number[] = [];
  let matched = false;

  for (const entry of details) {
    if (!isRecord(entry)) continue;
    const index = inclusionIndexOf(entry.path);
    if (entry.path !== 'inclusions' && index === null) continue;
    matched = true;
    if (index !== null && !indexes.includes(index)) indexes.push(index);
    if (typeof entry.message === 'string' && entry.message !== '' && !messages.includes(entry.message)) {
      messages.push(entry.message);
    }
  }

  if (!matched) return null;
  return { message: messages.join(' '), indexes };
}

/**
 * Prose → kind, per path.
 *
 * Matched on distinctive fragments of BOTH the top-level and the detail
 * sentence, because either one alone is enough to identify the refusal and the
 * backend has already reworded one of them once. Patterns are deliberately
 * loose about everything except the phrase that identifies the branch: they are
 * choosing supporting copy, not deciding whether to show the error. A miss
 * costs a generic sentence; it never costs the error.
 *
 * ⚠️ **Order matters within a path**, because the phrases are not all disjoint.
 * `cities-on-projectless-product` matches on "not sold through projects", which
 * contains `project-required`'s "sold through projects" as a substring — they
 * are separated only by being on different paths. Keep each list narrowest-first
 * and do not merge the two paths into one flat table.
 *
 * ⚠️ **Every pattern here was checked against `project-line.guard.ts` itself,
 * not against a published table.** Three of the backend's own documents listed
 * these wrongly and disagreed with each other; one still lists a refusal that
 * their code does not throw. Their source is the authority — if a written
 * answer ever contradicts it, the source is what runs.
 */
const KIND_PATTERNS: Readonly<
  Record<LineRefusalPath, ReadonlyArray<readonly [LineRefusalKind, RegExp]>>
> = {
  projectId: [
    ['project-missing', /does not exist|no project with id/i],
    ['project-retired', /no longer active|has been retired/i],
    ['project-wrong-product', /is not for product|this project sells product/i],
    /*
     * REFUSAL 1 — the first the guard reaches, and the one whose `path` was
     * least obvious: it fires on a line with NO project and still marks
     * `projectId`, because the missing project is the thing to supply. Read off
     * the guard source rather than assumed.
     */
    ['project-required', /sold through projects|delivered through projects/i],
  ],
  cityIds: [
    /*
     * REFUSAL 6, and the reason this list is not just "the city refusals".
     *
     * It shares `path: 'cityIds'` with refusal 7 and points at the opposite
     * screen — the project is empty, so the fix is on `/projects/{id}` and not
     * in this form. See the header. Before this kind existed it fell into
     * `unknown`, which was CORRECT (right control, server's own words) but
     * collapsed it with 7 for any caller reading `kind`, and left the guidance
     * pointing at a city picker that is empty by definition.
     *
     * Anchored on "declares no cities" / "add cities to this project", which
     * share no phrase with 7's "are not among the cities" / "not declared on
     * this project" — the two are disjoint on both sentences, so order between
     * them is belt-and-braces rather than load-bearing. It is still first,
     * because a future rewording is more likely to blur them than to separate
     * them further.
     */
    ['project-has-no-cities', /declares no cities|add cities to this project/i],
    ['cities-not-on-project', /are not among the cities|not declared on this project/i],
    /*
     * REFUSAL 2 — the product has no projects at all, so "choose a project"
     * would be advice that cannot be followed. Path read off the same guard
     * source, which throws `path: 'cityIds'` here.
     */
    ['cities-on-projectless-product', /not sold through projects|not delivered through projects/i],
    /*
     * ⚠️ A `cities-without-project` branch stood here, matching
     * `/this line has no project|has no project/i`. **It was a phantom, and it
     * has been deleted.**
     *
     * `This line has no project, so no cities can be named on it` — with the
     * detail `Choose a project on this line before naming the cities it runs
     * in` — is listed as refusal 1 (THEIR numbering) in `docs/api/quotes.md`,
     * `docs/api/invoices.md` and `docs/api/deals.md`. It is in none of the seven
     * `throw`s in `project-line.guard.ts`. A line with no project is refused by
     * OUR 1 or 2, both of which name the PRODUCT, and neither sentence contains
     * the phrase this pattern was waiting for.
     *
     * Recorded rather than quietly removed, because a matcher for a sentence
     * the server cannot send is the one kind of dead code that never announces
     * itself: it costs nothing, breaks nothing, passes every test, and looks
     * live for ever — the only proof it is dead is a refusal that never
     * arrives. If it is ever re-added it must come with a real response body,
     * not a documentation table.
     */
  ],
  /*
   * The REGION refusal, and the only entry in this table that exists as a
   * FALLBACK rather than as the discriminator.
   *
   * It carries `INCLUSION_NOT_OFFERED_IN_REGION`, so {@link lineRefusalOf}
   * reaches it from `error.code` and never gets here on a current server. This
   * pattern is what a client running against a server that has the refusal and
   * not the code would use — the same reason the two city codes still have
   * patterns above.
   *
   * ⚠️ **The wording is UNMEASURED.** It is anchored on the two fragments the
   * contract itself is phrased in ("not offered in", "region") rather than on a
   * sentence anybody has seen on the wire. A miss costs `kind: 'unknown'` with
   * the server's own words on the right control, which is this file's designed
   * degradation; it never costs the refusal. Do not widen it towards a bare
   * `/region/i` to make it "safer" — that would claim the three-destination
   * routing for any future refusal that merely mentions a region.
   */
  inclusions: [
    ['inclusion-not-offered-in-region', /not offered in .*region|region[^.]*does not offer/i],
  ],
};

/**
 * kind → which screen the fix is on. See {@link LineRefusalFix}.
 *
 * A total `Record`, deliberately: a new kind cannot be added to
 * {@link LineRefusalKind} without answering this question here, which is the
 * whole reason callers read {@link LineRefusal.fixedOn} instead of switching on
 * `kind` with a `default`.
 *
 * Only refusal 6 leaves this form. Everything else — including "the product has
 * no projects", where the fix is to stop naming cities on the line — is
 * actionable in the line editor.
 */
const FIX_FOR: Readonly<Record<LineRefusalKind, LineRefusalFix>> = {
  'project-required': 'this-line',
  'cities-on-projectless-product': 'this-line',
  'project-missing': 'this-line',
  /*
   * The record is fine and the CHOICE is not — the guard says so in its own
   * comment. Reactivating the project is possible on `/projects/{id}`, but it is
   * the rarer intent and picking a live project is the ordinary fix, so this
   * stays `this-line`.
   */
  'project-retired': 'this-line',
  'project-wrong-product': 'this-line',
  'project-has-no-cities': 'the-project',
  'cities-not-on-project': 'this-line',
  /*
   * ⚠️ The only THREE-destination answer in the table, and the reason
   * {@link LineRefusalFix} gained a member instead of this being `'this-line'`.
   *
   * Two of the three fixes are not on this line at all: widening the offer is an
   * admin action on `/products`, and giving the project its region is on
   * `/projects/{id}`. Answering `'this-line'` would send everybody to a picker
   * that will never list the inclusion they came for, and they would conclude
   * the app was broken rather than that nobody has offered it there yet.
   */
  'inclusion-not-offered-in-region': 'line-product-or-project',
  unknown: 'unknown',
};

/**
 * `error.code` → the kind it names, and the path it arrives on.
 *
 * **The contract half of the classification, and it is read BEFORE the prose.**
 * Only the refusals that actually carry a code appear here — the other five
 * share `UNPROCESSABLE_ENTITY`, which is what every zod failure on this API
 * carries, so listing them would claim a discrimination the envelope cannot
 * make.
 *
 * ⚠️ **A code absent from this map is "newer than this file", never "not a
 * refusal".** The lookup falls through to {@link KIND_PATTERNS}, and an
 * unrecognised sentence falls through to `kind: 'unknown'` — the degradation
 * this file's header calls its load-bearing behaviour. Do not add a default.
 *
 * `path` is what the code implies the detail will be on; it is used only to
 * check that the code and the detail agree, never to invent a control the
 * server did not name.
 */
const KIND_BY_CODE: ReadonlyMap<string, { kind: LineRefusalKind; path: LineRefusalPath }> = new Map(
  [
    [PROJECT_HAS_NO_CITIES_CODE, { kind: 'project-has-no-cities', path: 'cityIds' }],
    [CITIES_NOT_ON_PROJECT_CODE, { kind: 'cities-not-on-project', path: 'cityIds' }],
    [
      INCLUSION_NOT_OFFERED_IN_REGION_CODE,
      { kind: 'inclusion-not-offered-in-region', path: 'inclusions' },
    ],
  ] as const,
);

function kindOf(path: LineRefusalPath, message: string, summary: string): LineRefusalKind {
  const haystack = `${summary}\n${message}`;
  for (const [kind, pattern] of KIND_PATTERNS[path]) {
    if (pattern.test(haystack)) return kind;
  }
  return 'unknown';
}

/* -------------------------------------------------------------------------
 * Refusal 7's offending ids
 * ---------------------------------------------------------------------- */

/*
 * Both patterns are ANCHORED to the whole sentence, and that is the entire
 * trade-off in this section.
 *
 * A loose "pull the numbers out" would be worse than useless here. The
 * top-level sentence names the PROJECT id as well as the cities, so a bare
 * digit sweep returns a city that does not exist; and a sentence with anything
 * appended would yield a PARTIAL list a form then renders as if it were
 * complete, marking two chips and leaving a third wrong one unmarked. The
 * person fixes what is marked, resubmits, and is refused again with no idea
 * why.
 *
 * So: match the exact known shapes or return `[]`. `[]` costs the caller its
 * chip highlighting and it still shows the server's sentence, which names the
 * ids in plain text. A partial list that looks complete has no such floor.
 *
 * ⚠️ The concrete case this refuses to guess through: a truncated sentence such
 * as `Cities 4, 9 and 3 others are not among…`. `NOT_AMONG` requires the id run
 * to be followed immediately by `are not among`, so `and 3 others` fails the
 * anchor and the whole thing yields `[]` — never `[4, 9]`. Marking two chips and
 * leaving a third unmarked refuses the user a SECOND time after they have
 * already "fixed" it, which is worse than never highlighting anything.
 *
 * Refusal 6 cannot reach any of this: it is gated out by `kind` in
 * {@link lineRefusalOf}, and even unguarded neither pattern matches it —
 * `Project 7 declares no cities…` has no id run in either shape. That is
 * belt-and-braces, not a reason to remove the gate.
 */

/** `Not declared on this project: 4, 9` */
const NOT_DECLARED = /not declared on this project:\s*([0-9][0-9,\s]*?)\s*$/i;

/** `Cities 4, 9 are not among the cities project 7 runs in` */
const NOT_AMONG =
  /^\s*cities\s+([0-9][0-9,\s]*?)\s+are not among the cities project\s+[0-9]+\s+runs in\s*\.?\s*$/i;

/**
 * A comma-separated id list, or `[]` on any doubt.
 *
 * Every token must be a bare positive integer. One that is not means the
 * sentence is not the shape we think it is, so the whole list is discarded
 * rather than partially trusted.
 */
function parseCityIdList(raw: string): number[] {
  const tokens = raw
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
  if (tokens.length === 0) return [];

  const ids: number[] = [];
  for (const token of tokens) {
    if (!/^[0-9]+$/.test(token)) return [];
    const id = Number(token);
    if (!Number.isSafeInteger(id) || id <= 0) return [];
    ids.push(id);
  }
  return ids;
}

/*
 * The capture group is typed `string | undefined` under
 * `noUncheckedIndexedAccess`, and that is not merely a compiler formality here:
 * a group that matched empty is exactly the "the sentence is not the shape we
 * think it is" case this section refuses to guess through. Absent reads as no
 * match, which falls through to `[]`.
 */
function capture(match: RegExpExecArray | null): string | null {
  const group = match?.[1];
  return typeof group === 'string' && group !== '' ? group : null;
}

function offendingCityIdsFrom(message: string, summary: string): number[] {
  const declared = capture(NOT_DECLARED.exec(message)) ?? capture(NOT_DECLARED.exec(summary));
  if (declared !== null) {
    const ids = parseCityIdList(declared);
    if (ids.length > 0) return ids;
  }

  const among = capture(NOT_AMONG.exec(summary)) ?? capture(NOT_AMONG.exec(message));
  if (among !== null) return parseCityIdList(among);

  return [];
}

/* -------------------------------------------------------------------------
 * The entry point
 * ---------------------------------------------------------------------- */

/**
 * Classify a line-item write refusal.
 *
 * **`null` means "not one of these", and the caller MUST fall back to its
 * ordinary error handling** — the form-level banner, `formMessage`, whatever it
 * already does. Never to a blank or a guess.
 *
 * `null` is returned for exactly one reason: neither a recognised `error.code`
 * nor a `details[]` entry named `projectId`, `cityIds` or `inclusions`. An
 * unrecognised SENTENCE never produces it — that is `kind: 'unknown'` with the
 * path and the words preserved, `fixedOn: 'unknown'`, and the right control
 * still marked. **An unrecognised CODE never produces it either**; the code map
 * is consulted first and falls through to the prose, so a code newer than this
 * file is simply a code this file does not use.
 *
 * **Do not "tidy" that into `null`.** A sixth refusal landed between the brief
 * for this file and its commit, and a seventh — the one this doc comment used
 * to call a sixth — was found only because the model reported it as `unknown`
 * from the outside instead of dropping it. An eighth will be handled the same
 * way with no edit here. See the header section on the unknown bucket.
 *
 * ## The CODE is consulted first, and the prose is now the fallback
 *
 * This reverses the order the file shipped with, and the reason is the one the
 * header already argued for: **the code is the contract and the sentence is
 * not.** {@link KIND_BY_CODE} is read before {@link KIND_PATTERNS}, so a
 * rewording on the server can no longer change which kind a coded refusal
 * resolves to.
 *
 * The prose patterns are **kept, not superseded**, because they are what a
 * client sees against an older server — and because five of the refusals still
 * carry the shared `UNPROCESSABLE_ENTITY`, which is also what every zod failure
 * on this API carries, so for those the code separates nothing and `path` plus
 * the sentence remains the only discriminator.
 *
 * Note what this deliberately does NOT check:
 *
 * - **not `error.status`.** The path is the contract, and if the backend ever
 *   re-classifies one of these the guidance should not silently disappear —
 *   the same reasoning `quoteRefusalOf` records.
 *
 * An ORDINARY validation failure on either field — `projectId` sent as a
 * string, say — also comes back here, as
 * `kind: 'unknown'` with the server's sentence. That is the right outcome
 * rather than a leak. The caller marks the control the server named and shows
 * what the server said, which is exactly what it would have done anyway.
 */
export function lineRefusalOf(error: ApiError): LineRefusal | null {
  const summary = typeof error.message === 'string' ? error.message : '';

  /*
   * 1. The CODE, which is the contract.
   *
   * ⚠️ The region refusal is recognised from its code even when the details
   * array is not the shape expected, because the code alone is enough to know
   * WHERE to send the person — and that routing is the entire reason it has a
   * code. A refusal reported with no sentence beside it would be useless; a
   * refusal dropped because the details array was shaped unexpectedly would be
   * invisible, which is worse.
   */
  const coded = KIND_BY_CODE.get(error.code);
  if (coded !== undefined && coded.kind === 'inclusion-not-offered-in-region') {
    const inclusionDetails = inclusionRefusalDetails(error);
    return {
      path: 'inclusions',
      kind: coded.kind,
      fixedOn: FIX_FOR[coded.kind],
      message: inclusionDetails?.message || summary,
      summary,
      offendingCityIds: [],
      offendingInclusionIndexes: inclusionDetails?.indexes ?? [],
    };
  }

  const detail = firstRefusalDetail(error);
  if (detail === null) return null;

  const message = detail.message || summary;
  /*
   * A coded refusal keeps its code's kind; everything else falls through to the
   * prose. The path still comes from the DETAIL rather than from the code map,
   * because the detail is what a form marks and the server is the authority on
   * which control it named.
   */
  const kind =
    coded !== undefined && coded.path === detail.path
      ? coded.kind
      : kindOf(detail.path, message, summary);

  return {
    path: detail.path,
    kind,
    fixedOn: FIX_FOR[kind],
    message,
    summary,
    /*
     * Only refusal 7 carries ids, and only refusal 7 is asked for them. Parsing
     * unconditionally would let a reworded sentence on some other branch
     * produce a list of chips to mark on a refusal that is not about chips —
     * refusal 6 being the live example, since it names a project id on the very
     * same path.
     */
    offendingCityIds:
      kind === 'cities-not-on-project' ? offendingCityIdsFrom(message, summary) : [],
    /*
     * Reached only when the region refusal arrives WITHOUT its code — an older
     * server, or a rollback — and the prose matcher recognised it. On a current
     * server the coded branch above has already returned.
     */
    offendingInclusionIndexes:
      kind === 'inclusion-not-offered-in-region'
        ? (inclusionRefusalDetails(error)?.indexes ?? [])
        : [],
  };
}

/* -------------------------------------------------------------------------
 * The post-merge trap, expressed as code
 * ---------------------------------------------------------------------- */

/**
 * Whether this PATCH will be judged against cities the user never sent.
 *
 * `true` when the body moves the line's project and says nothing about its
 * cities — the exact shape that lets refusal 7 come back naming cities that are
 * not on screen. The fix is never to catch it better; it is to send
 * `{ projectId, cityIds }` together, having first shown the person what
 * {@link citiesLostByMovingProject} says they are about to lose.
 *
 * Takes the patch body rather than form state, so the question is asked of the
 * thing that will actually be sent. Uses `in`, not truthiness: `projectId:
 * null` clears the project and is a real instruction, and `cityIds: []` clears
 * the set and is also a real instruction. Both are falsy and neither is absent.
 *
 * ⚠️ **`allCities` counts as expressing the cities too** (migration 083). A
 * patch carrying it is not leaving the stored set to be re-checked against a
 * new project — it is replacing how this line answers the question entirely,
 * and a flagged line stores no explicit rows to re-check. Omitting it from this
 * test would warn about stranding cities that cannot be stranded.
 *
 * ⚠️ **NOTHING CALLS THIS TODAY** — the only mention across `src/` is a
 * comment. It is kept because the question it asks is real
 * and easy to get wrong, but do not read its existence as evidence that some
 * screen is relying on it. If you are here because you are about to depend on
 * it, exercise it first: it has never run.
 */
export function patchRevalidatesStoredCities(patch: object): boolean {
  return 'projectId' in patch && !('cityIds' in patch) && !('allCities' in patch);
}

/**
 * Whether this PATCH will be judged against INCLUSIONS the user never sent.
 *
 * The same post-merge trap as {@link patchRevalidatesStoredCities}, on the axis
 * migration 096 added: a product may offer an inclusion **only in certain
 * regions**, and a line's region comes from its project — so moving the line to
 * a project in another region re-validates the inclusions the line ALREADY
 * holds and can answer `422 INCLUSION_NOT_OFFERED_IN_REGION` naming a field the
 * caller never touched.
 *
 * `true` when the body moves the line's project and says nothing about its
 * inclusions. The fix is never to catch it better; it is to send
 * `{ projectId, inclusions }` together, exactly as `{ projectId, cityIds }`
 * already travels.
 *
 * ⚠️ **`true` is NOT on its own an instruction to add `inclusions: []`.** An
 * inclusions write REPLACES the whole set, so an empty array DESTROYS a line's
 * stored rows with a 200 and nothing to restore from. Where the inclusions
 * editor is not rendered — the product offers none — the correct payload still
 * OMITS the key and accepts that the refusal, if it comes, cannot be fixed from
 * this line. That is a real and unavoidable state, and it is the one this
 * function must not be read as licensing a `[]` for.
 *
 * Uses `in`, not truthiness, for the same reason as the cities version:
 * `projectId: null` clears the project and is a real instruction.
 */
export function patchRevalidatesStoredInclusions(patch: object): boolean {
  return 'projectId' in patch && !('inclusions' in patch);
}

/**
 * The cities a line would lose by being moved to another project.
 *
 * A form calls this with the line's stored city ids and the target project's
 * own `cities[].id`, and can then say "moving this line to Autumn 2027 India
 * drops Mumbai and Pune" — before sending, in the user's own words, instead of
 * letting the server refuse with a list of bare ids the form then has to
 * translate back.
 *
 * Order follows the STORED set, so the ids line up with the chips already on
 * screen. Duplicates collapse, matching what the server does with a repeated
 * `cityIds` entry.
 *
 * ⚠️ **Do not call this for a line whose `allCities` is true, and do not feed
 * it `line.cities`.** Those are two ways of making the same mistake. A flagged
 * line stores NO explicit rows and resolves against the project on every read,
 * so moving it to another project strands nothing — it simply resolves to the
 * new project's cities. But its `cities` array is the RESOLVED set and looks
 * exactly like a stored one, so passing it here produces a confident list of
 * cities "lost" that the move would not lose, and the warning names real city
 * names, which is what makes it convincing.
 *
 * The argument is named `storedCityIds` rather than `cityIds` for this reason.
 * Ask `allCities` first; if it is true there is nothing to compute.
 *
 * ⚠️ **The one caller today is `DealFormModal`'s line editor**
 * (`components/deals/LineItemFormModal.tsx`), and it is safe for a reason that
 * expires: it passes the form's own explicit selection state, and DEAL lines
 * have no `allCities` at all — migration 083 landed on `f_quote_products` and
 * `f_invoice_products` only. The day deal lines gain the flag, that call starts
 * producing the false warning described above, and nobody is watching it. The
 * two document grids do not call this at all; they clear the selection when the
 * project changes instead.
 */
export function citiesLostByMovingProject(
  storedCityIds: readonly number[],
  targetProjectCityIds: readonly number[],
): number[] {
  const allowed = new Set(targetProjectCityIds);
  const lost: number[] = [];
  const seen = new Set<number>();
  for (const cityId of storedCityIds) {
    if (allowed.has(cityId) || seen.has(cityId)) continue;
    seen.add(cityId);
    lost.push(cityId);
  }
  return lost;
}
